import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { fetchRobots, fetchRobotThumbnailUrl, deleteRobot, updateRobot, Robot } from '../api/robots';

const DEFAULT_ROBOTS: Robot[] = [
  {
    id: 'built-in-go2',
    name: 'Unitree Go2',
    status: 'ready',
    description: 'Quadruped robot — standing balance & locomotion',
    created_at: '',
  },
];

interface RobotCardProps {
  robot: Robot;
  isDefault?: boolean;
  onClick: () => void;
  onEdit?: (robot: Robot) => void;
  onDelete?: (robot: Robot) => void;
}

const RobotCard: React.FC<RobotCardProps> = ({ robot, isDefault, onClick, onEdit, onDelete }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (isDefault) {
      setThumbnailUrl('/robots/go2-thumbnail.png');
    } else if (robot.thumbnail_s3_key) {
      fetchRobotThumbnailUrl(robot.id)
        .then(setThumbnailUrl)
        .catch(() => {});
    }
  }, [robot.id, robot.thumbnail_s3_key, isDefault]);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        backgroundColor: '#0a0a0a',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Kebab menu — user-created robots only */}
      {!isDefault && (
        <>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              zIndex: 2,
              color: 'rgba(255,255,255,0.5)',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => handleMenuClose()}
            onClick={(e) => e.stopPropagation()}
            slotProps={{ paper: { sx: { bgcolor: '#1a1a1a', color: '#fff' } } }}
          >
            <MenuItem onClick={(e) => { handleMenuClose(e); onEdit?.(robot); }}>
              Edit
            </MenuItem>
            <MenuItem onClick={(e) => { handleMenuClose(e); onDelete?.(robot); }} sx={{ color: '#f44336' }}>
              Delete
            </MenuItem>
          </Menu>
        </>
      )}

      {/* Thumbnail area */}
      <Box
        sx={{
          aspectRatio: '16/9',
          background: isDefault
            ? 'linear-gradient(135deg, #0a0a0a 0%, rgba(239,137,151,0.12) 100%)'
            : 'linear-gradient(135deg, #0a0a0a 0%, rgba(255,255,255,0.04) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={robot.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <SmartToyOutlinedIcon
            sx={{ fontSize: 56, color: 'rgba(255,255,255,0.15)' }}
          />
        )}
        {isDefault && (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(239,137,151,0.2)',
              color: '#EF8997',
              px: 1,
              py: 0.25,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.05em',
            }}
          >
            BUILT-IN
          </Typography>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {robot.name}
        </Typography>
        {robot.description && (
          <Typography variant="caption" color="text.secondary">
            {robot.description}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const RobotsList: React.FC = () => {
  const navigate = useNavigate();
  const [robots, setRobots] = useState<Robot[]>([]);
  const [editRobot, setEditRobot] = useState<Robot | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Robot | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadRobots = () => {
    fetchRobots()
      .then(setRobots)
      .catch(() => {});
  };

  useEffect(() => { loadRobots(); }, []);

  // Filter out failed/converting robots
  const readyRobots = robots.filter((r) => r.status === 'ready');
  const allRobots = [...DEFAULT_ROBOTS, ...readyRobots];

  const handleEdit = (robot: Robot) => {
    setEditRobot(robot);
    setEditName(robot.name);
    setEditDesc(robot.description || '');
  };

  const handleEditSave = async () => {
    if (!editRobot) return;
    await updateRobot(editRobot.id, { name: editName, description: editDesc || undefined });
    setEditRobot(null);
    loadRobots();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRobot(deleteTarget.id);
      setDeleteTarget(null);
      loadRobots();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" sx={{ mb: 1, fontWeight: 600 }}>
            Robots
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Built-in and custom robot models for training
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/robots/upload')}
          sx={{
            backgroundColor: '#EF8997',
            '&:hover': { backgroundColor: '#e5707f' },
          }}
        >
          Add Robot
        </Button>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 3,
        }}
      >
        {allRobots.map((robot) => (
          <RobotCard
            key={robot.id}
            robot={robot}
            isDefault={robot.id.startsWith('built-in-')}
            onClick={() => navigate(`/robots/${robot.id}`)}
            onEdit={handleEdit}
            onDelete={(r) => setDeleteTarget(r)}
          />
        ))}
      </Box>

      {/* Edit dialog */}
      <Dialog
        open={Boolean(editRobot)}
        onClose={() => setEditRobot(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: '#fff' } }}
      >
        <DialogTitle>Edit Robot</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={editDesc}
            onChange={(e) => setEditDesc(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRobot(null)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" sx={{ bgcolor: '#EF8997', '&:hover': { bgcolor: '#e5707f' } }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: '#fff' } }}
      >
        <DialogTitle>Delete Robot</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RobotsList;
